/**
 * deliveryRpmSlice
 *
 * Caches the reason-code catalogues fetched (in the currently selected app
 * language) when DeliveryRPMScreen mounts, and re-fetched whenever the
 * language changes. Consumed read-only everywhere else:
 *
 *   haulbackRcMap     — Record<id, reasonCode>       label lookup for tables/modals (One Way)
 *   haulbackDtoMap    — Record<id, HaulbackDtoEntry>  full entry for pictureRequired / textFieldRequired flags
 *   roadblockRcMap    — Record<id, routeRoadblock>    label lookup for filters/modals (Route Roadblocks)
 *   roadblockTypeMap  — Record<id, roadblockType>     translated "Reason" column value (Route Roadblocks)
 *
 * Both tables are small (<200 rows) and change rarely, so a simple slice
 * suffices — no pagination, no expiry logic needed. `cataloguesLanguage`
 * tracks which language the maps above were last fetched in, so callers can
 * detect a language change and trigger a re-fetch.
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';

/** Minimal shape stored per haulback reason-code entry. */
export interface HaulbackDtoEntry {
  reasonCode: string;
  pictureRequired: boolean;
  textFieldRequired: boolean;
}

interface DeliveryRpmState {
  /** id → reasonCode label for Haulback submissions */
  haulbackRcMap: Record<number, string>;
  /** id → full haulback catalogue entry (for pictureRequired / textFieldRequired) */
  haulbackDtoMap: Record<number, HaulbackDtoEntry>;
  /** id → routeRoadblock label for Route Roadblock submissions */
  roadblockRcMap: Record<number, string>;
  /** id → roadblockType label ('Stop Issue' | 'Route Issue', translated) for Route Roadblock submissions */
  roadblockTypeMap: Record<number, string>;
  /** language code the catalogues above were last fetched in (null = not yet loaded) */
  cataloguesLanguage: string | null;
}

const initialState: DeliveryRpmState = {
  haulbackRcMap: {},
  haulbackDtoMap: {},
  roadblockRcMap: {},
  roadblockTypeMap: {},
  cataloguesLanguage: null,
};

const deliveryRpmSlice = createSlice({
  name: 'deliveryRpm',
  initialState,
  reducers: {
    setHaulbackRcMap: (state, action: PayloadAction<Record<number, string>>) => {
      state.haulbackRcMap = action.payload;
    },
    setHaulbackDtoMap: (state, action: PayloadAction<Record<number, HaulbackDtoEntry>>) => {
      state.haulbackDtoMap = action.payload;
    },
    setRoadblockRcMap: (state, action: PayloadAction<Record<number, string>>) => {
      state.roadblockRcMap = action.payload;
    },
    setRoadblockTypeMap: (state, action: PayloadAction<Record<number, string>>) => {
      state.roadblockTypeMap = action.payload;
    },
    setCataloguesLanguage: (state, action: PayloadAction<string | null>) => {
      state.cataloguesLanguage = action.payload;
    },
  },
});

export const {
  setHaulbackRcMap,
  setHaulbackDtoMap,
  setRoadblockRcMap,
  setRoadblockTypeMap,
  setCataloguesLanguage,
} = deliveryRpmSlice.actions;

export default deliveryRpmSlice.reducer;
